/// <reference types="vite/client" />
import { getRuntimeConfig, type RuntimeEnv } from './runtime-env'

// Re-export for convenience
export type { RuntimeConfig, RuntimeEnv } from './runtime-env'
export { getRuntimeConfig } from './runtime-env'

/**
 * Get the current runtime environment
 */
function getEnv(): RuntimeEnv {
  return getRuntimeConfig().env
}

/**
 * `true` in production (app.gtowizard.com) or on the prod-equivalent gtowiz.com
 * staging env — both behave like production (live payments, prod analytics/OAuth).
 * Note: This is a getter function to avoid evaluating at module load time.
 */
export function IS_PROD_ENV(): boolean {
  const env = getEnv()

  return env === 'production' || env === 'staging'
}

/**
 * `true` only on the gtowiz.com staging env (distinct from the flagship prod app).
 * Note: This is a getter function to avoid evaluating at module load time.
 */
export function IS_STAGING_ENV(): boolean {
  return getEnv() === 'staging'
}

/**
 * `true` when app is running in:
 * - development mode build - https://app.dev.gtowizard.com/
 * - additional dev environments - https://app.<name>.dev.gtowizard.com/
 * - PR deployment environment - https://{branch-name}-cws.pages.dev/
 * Note: This is a getter function to avoid evaluating at module load time.
 */
export function IS_DEV_ENV(): boolean {
  const env = getEnv()

  return env === 'development' || env === 'preview'
}

/**
 * `true` when app is running in local development mode - http://localhost:8080 or http://localhost:8000
 * Note: This is a getter function to avoid evaluating at module load time.
 */
export function IS_LOCAL_ENV(): boolean {
  return getEnv() === 'local'
}

export function IS_LOCAL_REACT_ENV(): boolean {
  return IS_LOCAL_ENV() && import.meta.env.VITE_REACT_ONLY === 'true'
}

/**
 * `true` when app is running in Cloudflare Pages preview environment
 * Note: This is a getter function to avoid evaluating at module load time.
 */
export function IS_PREVIEW_ENV(): boolean {
  return getEnv() === 'preview'
}

/**
 * `true` when the app is targeting the production API, either because:
 * - it is deployed at app.gtowizard.com (IS_PROD_ENV), OR
 * - it is running locally with VITE_PROXY_URL=https://api.gtowizard.com/
 *
 * Use to gate dev-only UI (e.g. the magic-login icon) that must never appear
 * when the user could authenticate against real production data.
 */
export function IS_PROD_API_TARGET(): boolean {
  if (IS_PROD_ENV()) {
    return true
  }
  const proxy = import.meta.env.VITE_PROXY_URL
  if (typeof proxy !== 'string' || proxy.length === 0) {
    return false
  }
  try {
    return new URL(proxy).hostname === 'api.gtowizard.com'
  } catch {
    return false
  }
}
